/**
 * Typed client for the IdeaBrief API. Platform-agnostic: uses only `fetch`,
 * so the same code runs in the browser and in Expo.
 */
import type {
  ApiErrorBody,
  Idea,
  IdeasResponse,
  MeResponse,
} from './types.js';
import { ApiError } from './types.js';
import type { CreateIdeaInput } from './schemas.js';

export interface ApiClientOptions {
  /**
   * API origin. Empty string on web (same-origin, so cookies flow naturally);
   * the deployed Worker URL in Expo.
   */
  baseUrl?: string;
  /**
   * Supplies an Authorization header on mobile, where cookies aren't used.
   * Omitted on web — the session cookie travels automatically.
   */
  getAuthHeader?: () => string | null | Promise<string | null>;
}

export function createApiClient(options: ApiClientOptions = {}) {
  const baseUrl = options.baseUrl ?? '';

  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body !== undefined) {
      headers.set('Content-Type', 'application/json');
    }

    if (options.getAuthHeader) {
      const auth = await options.getAuthHeader();
      if (auth) headers.set('Authorization', auth);
    }

    const res = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers,
      // Sends the session cookie on web. `credentials` is a browser-only
      // RequestInit field and is absent from the Workers lib types, so it is
      // attached here rather than declared inline — this file is compiled
      // under both DOM and Workers type sets.
      ...({ credentials: 'include' } as object),
    });

    if (!res.ok) {
      throw await toApiError(res);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    getMe: () => request<MeResponse>('/api/me'),
    getIdeas: () => request<IdeasResponse>('/api/ideas'),
    createIdea: (input: CreateIdeaInput) =>
      request<Idea>('/api/ideas', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    deleteAccount: () => request<void>('/api/me', { method: 'DELETE' }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * Converts a failed response into a typed ApiError. Falls back to a generic
 * message when the body isn't the expected shape — the server never leaks
 * internal detail, so neither do we.
 */
async function toApiError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    if (body?.error?.code) {
      return new ApiError(body.error.code, body.error.message, res.status);
    }
  } catch {
    // Body wasn't JSON — fall through.
  }
  return new ApiError('internal', 'Something went wrong. Try again.', res.status);
}
